import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { nitrologo } from "../utilities/images";
import RideProfileDropdown from "../components/chooseride/RideProfileDropdown";
import SEO from "../components/SEO";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";
import "../components/chooseride/Go.css";
import "./RideHistory.css";

const RideHistory = () => {
  const navigate = useNavigate();
  const { currentUser, riderData, signOut } = useAuth();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'completed' | 'cancelled' | 'in-progress'
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Fetch real-time user trips from Firebase RTDB "trips" node
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const tripsRef = ref(db, `trips`);
    const unsubscribe = onValue(
      tripsRef,
      (snapshot) => {
        const data = snapshot.val();
        let fetchedRides = [];

        if (data) {
          Object.keys(data).forEach((key) => {
            const trip = data[key];
            if (trip.riderId === currentUser.uid || trip.userId === currentUser.uid) {
              // Format timestamps
              let dateStr = "Recent trip";
              if (trip.timestamps?.requestedAt) {
                const reqDate = new Date(trip.timestamps.requestedAt);
                dateStr = `${reqDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })} • ${reqDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`;
              } else if (trip.timestamps?.startedAt) {
                const reqDate = new Date(trip.timestamps.startedAt);
                dateStr = `${reqDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}`;
              }

              // Format fare total
              let formattedFare = "₦0.00";
              if (typeof trip.fare === "number") {
                formattedFare = `₦${trip.fare.toLocaleString()}`;
              } else if (trip.fare?.total !== undefined) {
                formattedFare = `₦${Number(trip.fare.total).toLocaleString()}`;
              } else if (typeof trip.fare === "string") {
                formattedFare = trip.fare.startsWith("₦") ? trip.fare : `₦${trip.fare}`;
              }

              // Normalize status
              const rawStatus = (trip.status || "completed").toLowerCase();
              let normalizedCategory = "completed";
              if (rawStatus.includes("cancel")) {
                normalizedCategory = "cancelled";
              } else if (
                rawStatus.includes("driver") ||
                rawStatus.includes("driving") ||
                rawStatus.includes("trip") ||
                rawStatus.includes("search") ||
                rawStatus.includes("request")
              ) {
                normalizedCategory = "in-progress";
              }

              // Extract chat count if any
              const chatMessages = trip.chat ? Object.values(trip.chat) : [];

              fetchedRides.push({
                id: key,
                date: dateStr,
                pickup: trip.pickup || { address: "Pickup Location" },
                dropoff: trip.destination || trip.dropoff || { address: "Destination Location" },
                fare: formattedFare,
                status: trip.status || "completed",
                statusCategory: normalizedCategory,
                vehicleType: trip.vehicleType || "9tro Ride",
                driverId: trip.driverId,
                distanceKm: trip.route?.distanceKm,
                durationMin: trip.route?.durationMin,
                chatCount: chatMessages.length,
                rawTrip: trip,
              });
            }
          });
        }

        // Strictly show only current user's trips
        setRides(fetchedRides);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching trips:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Filter rides based on active tab
  const filteredRides = rides.filter((ride) => {
    if (activeFilter === "all") return true;
    const cat = (ride.statusCategory || ride.status || "").toLowerCase();
    return cat === activeFilter;
  });

  const formatStatusText = (status) => {
    if (!status) return "COMPLETED";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const handleRebook = (ride) => {
    if (ride.pickup?.address && ride.dropoff?.address) {
      navigate(
        `/go/from/${encodeURIComponent(ride.pickup.address)}/to/${encodeURIComponent(ride.dropoff.address)}`,
        {
          state: {
            pickup: ride.pickup,
            dropoff: ride.dropoff,
          },
        }
      );
    } else {
      navigate("/");
    }
  };

  return (
    <div className="ride-history-page">
      <SEO
        title="Ride History & Activity"
        description="Review your past 9tro Ride trips, rebook routes, check fares, and download receipts."
        url="https://www.9troride.com/ride-history"
      />
      {/* Header Navbar (Go.jsx Navbar) */}
      <header className="go-header">
        <div className="go-header-left">
          <button onClick={() => navigate(-1)} className="go-header-back-btn" aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <Link to="/">
            <img src={nitrologo} alt="9troRide Logo" className="go-header-logo" />
          </Link>
        </div>

        {currentUser ? (
          <div style={{ position: "relative" }} ref={profileDropdownRef}>
            <button
              onClick={() => setIsProfileDropdownOpen((prev) => !prev)}
              className="go-header-profile-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>
                {riderData?.first_name ||
                  currentUser.displayName?.split(" ")[0] ||
                  "Profile"}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {isProfileDropdownOpen && (
              <RideProfileDropdown
                user={currentUser}
                rider={riderData}
                onLogout={handleLogout}
              />
            )}
          </div>
        ) : (
          <Link to="/ride-login" className="go-header-login-btn">
            Log In
          </Link>
        )}
      </header>

      {/* Ride History Content */}
      <main className="ride-history-container">
        <div className="ride-history-header-section">
          <h1>Ride History</h1>
          <p>Review your past trips, rebook routes, or view receipts.</p>
        </div>

        {/* Filter Tabs */}
        <div className="ride-history-filters">
          <button
            className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All Rides
          </button>
          <button
            className={`filter-btn ${activeFilter === "completed" ? "active" : ""}`}
            onClick={() => setActiveFilter("completed")}
          >
            Completed
          </button>
          <button
            className={`filter-btn ${activeFilter === "cancelled" ? "active" : ""}`}
            onClick={() => setActiveFilter("cancelled")}
          >
            Cancelled
          </button>
          <button
            className={`filter-btn ${activeFilter === "in-progress" ? "active" : ""}`}
            onClick={() => setActiveFilter("in-progress")}
          >
            In Progress
          </button>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="ride-history-loading">
            <div className="history-spinner"></div>
            <p>Fetching your rides...</p>
          </div>
        ) : filteredRides.length === 0 ? (
          /* Empty State */
          <div className="ride-history-empty">
            <div className="empty-icon-wrap">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </div>
            <h3>No rides found</h3>
            <p>You have no {activeFilter !== "all" ? activeFilter : ""} trips yet.</p>
            <button className="book-ride-btn" onClick={() => navigate("/")}>
              Book a Ride Now
            </button>
          </div>
        ) : (
          /* Rides List */
          <div className="ride-cards-grid">
            {filteredRides.map((ride) => {
              const cat = (ride.statusCategory || ride.status || "").toLowerCase();
              const statusClass =
                cat === "completed"
                  ? "status-completed"
                  : cat === "cancelled"
                  ? "status-cancelled"
                  : "status-in-progress";

              return (
                <div key={ride.id} className="ride-history-card">
                  {/* Card Header */}
                  <div className="ride-card-top">
                    <div className="vehicle-info-group">
                      <span className="vehicle-badge">{ride.vehicleType || "9tro Ride"}</span>
                      <span className="ride-date">{ride.date || "Recent trip"}</span>
                    </div>
                    <span className={`status-pill ${statusClass}`}>
                      {formatStatusText(ride.status)}
                    </span>
                  </div>

                  {/* Route Timeline */}
                  <div className="ride-route-timeline">
                    <div className="timeline-dots">
                      <span className="dot pickup-dot"></span>
                      <span className="timeline-line"></span>
                      <span className="dot dropoff-dot"></span>
                    </div>
                    <div className="timeline-addresses">
                      <div className="address-row">
                        <span className="address-label">Pickup</span>
                        <p className="address-text">
                          {ride.pickup?.address || ride.pickupAddress || "Pickup location"}
                        </p>
                      </div>
                      <div className="address-row">
                        <span className="address-label">Dropoff</span>
                        <p className="address-text">
                          {ride.dropoff?.address || ride.dropoffAddress || "Dropoff location"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer Details & Actions */}
                  <div className="ride-card-bottom">
                    <div className="fare-detail">
                      <span className="fare-label">Total Fare</span>
                      <span className="fare-amount">{ride.fare || ride.price || "₦0.00"}</span>
                    </div>

                    <div className="ride-card-actions">
                      <button
                        className="receipt-btn"
                        onClick={() => setSelectedReceipt(ride)}
                      >
                        Receipt
                      </button>
                      <button
                        className="rebook-btn"
                        onClick={() => handleRebook(ride)}
                      >
                        Rebook
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Receipt Modal */}
      {selectedReceipt && (
        <div className="modal-backdrop" onClick={() => setSelectedReceipt(null)}>
          <div className="receipt-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-header">
              <h3>Trip Receipt</h3>
              <button
                className="receipt-close-btn"
                onClick={() => setSelectedReceipt(null)}
              >
                ✕
              </button>
            </div>

            <div className="receipt-body">
              <div className="receipt-summary-item">
                <span>Trip Date</span>
                <strong>{selectedReceipt.date || "Recent trip"}</strong>
              </div>
              <div className="receipt-summary-item">
                <span>Vehicle Type</span>
                <strong>{selectedReceipt.vehicleType || "9tro Comfort"}</strong>
              </div>
              <div className="receipt-summary-item">
                <span>Driver</span>
                <strong>{selectedReceipt.driverName || "9tro Partner Driver"}</strong>
              </div>
              <div className="receipt-summary-item">
                <span>Payment Method</span>
                <strong>{selectedReceipt.paymentMethod || "Card"}</strong>
              </div>

              <hr className="receipt-divider" />

              <div className="receipt-price-row">
                <span>Base Fare & Mileage</span>
                <span>{selectedReceipt.fare || "₦0.00"}</span>
              </div>
              <div className="receipt-price-row total-row">
                <strong>Total Paid</strong>
                <strong className="total-fare">{selectedReceipt.fare || "₦0.00"}</strong>
              </div>
            </div>

            <button
              className="receipt-done-btn"
              onClick={() => setSelectedReceipt(null)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RideHistory;
